import { useState } from "react";
import { useCart } from "../context/CartContext";
import { money, resolveImg, track } from "../lib/api";
import { X } from "lucide-react";

export const AddKitButton = ({ kit, className, testId, label = "Add Kit to Cart", onAdded }) => {
  const { addItem } = useCart();
  const [open, setOpen] = useState(false);
  const configurable = (kit.products || []).filter((p) => p.variations?.skus?.length > 1);
  const [selection, setSelection] = useState({});

  const defaults = () => {
    const sel = {};
    configurable.forEach((p) => {
      const sku = p.variations.skus.find((s) => s.inventory > 0) || p.variations.skus[0];
      sel[p.slug] = sku.sku_code;
    });
    return sel;
  };

  const skuLabel = (p, code) => {
    const sku = p.variations.skus.find((s) => s.sku_code === code);
    return sku ? Object.values(sku.specs || {}).join(" / ") : "";
  };

  const add = (componentSkus) => {
    const labels = configurable.map((p) => `${p.name.replace(/^Rothco /, "")}: ${skuLabel(p, componentSkus[p.slug])}`);
    addItem({
      item_type: "kit", slug: kit.slug, name: kit.name, price: kit.bundle_price, image: kit.image,
      component_skus: componentSkus, variant_label: labels.join(" · ") || null,
      contents: (kit.products || []).map((p) => ({ slug: p.slug, name: p.name, image: p.images?.[0] || null })),
    });
    track("kit_add_to_cart", "add_kit_button", { kit: kit.slug, level: kit.level });
    onAdded?.();
    setOpen(false);
  };

  const onClick = () => {
    if (configurable.length === 0) { add({}); return; }
    setSelection(defaults());
    setOpen(true);
  };

  return (
    <>
      <button data-testid={testId} onClick={onClick} className={className}>{label}</button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div data-testid="kit-options-dialog" className="relative bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto border border-black/10 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-heading font-bold uppercase tracking-tight text-lg">{kit.name}</h3>
                <p className="text-xs text-black/50 mt-1">Choose options for your kit components.</p>
              </div>
              <button data-testid="kit-options-close" onClick={() => setOpen(false)} aria-label="Close"><X size={18} /></button>
            </div>
            <div className="mt-5 space-y-5">
              {configurable.map((p) => (
                <div key={p.slug} className="flex gap-3 items-start">
                  <img src={resolveImg(p.images?.[0])} alt={p.name} className="w-14 h-14 object-cover border border-black/10 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold leading-snug">{p.name}</p>
                    <select data-testid={`kit-option-${p.slug}`} value={selection[p.slug] || ""}
                      onChange={(e) => setSelection({ ...selection, [p.slug]: e.target.value })}
                      className="mt-2 w-full border border-black/20 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A5D23]">
                      {p.variations.skus.map((s) => (
                        <option key={s.sku_code} value={s.sku_code} disabled={s.inventory <= 0}>
                          {Object.values(s.specs || {}).join(" / ")}{s.inventory <= 0 ? " — out of stock" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="kit-options-confirm" onClick={() => add(selection)}
              className="mt-6 w-full bg-[#4A5D23] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150">
              Add Kit to Cart — {money(kit.bundle_price)}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
